/**
 * Multi-resolution waveform peaks.
 *
 * For each channel we store several mip levels. Level 0 is the densest; each
 * subsequent level halves the resolution. Drawing picks the level whose bucket
 * width is closest to one screen pixel, so a five-minute file costs the same to
 * draw zoomed out as a five-second one, and zooming in never re-reads the whole
 * source buffer.
 *
 * Peaks are stored as interleaved min/max pairs in an Int8Array (-127..127),
 * which is 8x smaller than Float32 and visually indistinguishable at any zoom a
 * screen can show.
 */

export const WAVEFORM_VERSION = 1;

/** Samples per bucket at mip level 0. */
export const BASE_SAMPLES_PER_BUCKET = 256;

export interface WaveformLevel {
  /** Source samples represented by one bucket at this level. */
  samplesPerBucket: number;
  bucketCount: number;
  /** Interleaved [min, max] pairs per channel: channels[c][i*2], [i*2+1]. */
  channels: Int8Array[];
}

export interface WaveformPeaks {
  version: number;
  assetId: string;
  sampleRate: number;
  channelCount: number;
  durationSec: number;
  levels: WaveformLevel[];
}

/** Chooses the mip level whose buckets are closest to `targetSamplesPerPixel`. */
export function pickLevel(peaks: WaveformPeaks, targetSamplesPerPixel: number): WaveformLevel {
  let best = peaks.levels[0];
  for (const level of peaks.levels) {
    if (level.samplesPerBucket <= targetSamplesPerPixel) best = level;
    else break;
  }
  return best;
}
